import { useEffect, useId, useRef } from "react";

/**
 * Модальное окно кабинета.
 *
 * На НАТИВНОМ `<dialog>` с `showModal()`, а не на своём `div` с `position:
 * fixed`. Разница не в разметке, а в том, что браузер отдаёт бесплатно и что
 * иначе пришлось бы писать самому и потом чинить:
 *
 * - закрытие по Esc;
 * - ловушка фокуса: Tab не уходит на кнопки под окном;
 * - возврат фокуса туда, откуда окно открыли;
 * - верхний слой, то есть окно не может оказаться под чужим `z-index`;
 * - `inert` для остального документа, то есть читалка не читает фон.
 *
 * ⚠️ **Своих модальных поверхностей в приложении больше нет** (14.09.2026).
 * Проводник материалов, выбор файла, просмотр файла и фотография решения жили
 * на собственных `div` с `position: fixed`, и ни у одного не было ни Esc от
 * браузера, ни ловушки фокуса, ни `inert`: Tab уводил за спину окна, и человек
 * набирал в поле, которого не видит. Все четыре переехали сюда. Что осталось
 * своим, осталось по природе: поповер занятия, панели редактора заготовки и
 * меню проводника это не окна, они привязаны к месту на экране и закрываются
 * нажатием мимо. Сторожит `lib/dialog-role.test.ts`.
 *
 * Прокрутка фона гасится вручную: единственное, чего нативный диалог не делает,
 * это блокировка прокрутки под собой, и колесо мыши иначе крутит список за
 * окном.
 *
 * ⚠️ Гасится СЧЁТЧИКОМ, а не запомненным значением. Запомненное значение это
 * ловушка на два окна внахлёст: второе окно сохранило бы уже `hidden`, и после
 * закрытия обоих прокрутка фона осталась бы выключенной навсегда. Порядок
 * размонтирования при этом не наш: его выбирает React.
 */

/** Сколько окон сейчас держит прокрутку фона выключенной. */
let scrollLocks = 0;

function lockScroll(): () => void {
  if (scrollLocks === 0) document.body.style.overflow = "hidden";
  scrollLocks += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    scrollLocks -= 1;
    if (scrollLocks === 0) document.body.style.overflow = "";
  };
}
export function Dialog(props: {
  title: string;
  /** Подпись под заголовком: состояние, дата, что угодно короткое. */
  note?: React.ReactNode;
  /**
   * Вторая ширина, `--w-dlg-wide`. Выбирает ступень содержимое: обычная это
   * подтверждение и короткая форма, широкая это список или таблица.
   */
  wide?: boolean;
  /**
   * Третья ширина, `--w-dlg-media`, и роль у неё ДРУГАЯ, а не «ещё пошире».
   * Первые две меряют текст: строка формы и колонка таблицы читаются на своей
   * длине. Снимок тетрадного листа текстом не меряется вовсе, его читают
   * глазами, и разборчивость держится на числе пикселей. Вместе с шириной
   * отпускается и потолок высоты снимка.
   *
   * ⚠️ Четвёртой ширины нет, и заводить её на глаз нельзя: у каждой из трёх
   * названа мера, а не размер.
   */
  media?: boolean;
  /**
   * Лист снизу. ⚠️ Это **НЕ четвёртая ширина, а размещение**, и поэтому свойство
   * стоит отдельно от трёх выше, а не четвёртым в их ряду. Три ширины меряют
   * содержимое, у каждой названа мера; лист меряет место: ширина экрана,
   * скругление только сверху, прижат к нижней кромке на ЛЮБОЙ ширине.
   *
   * ⚠️ Правило листа у обычного окна (`@media (max-width: 528px)`) сюда не
   * годится, хотя выглядит тем же. Лист «Ещё» живёт только ниже 568 px, то есть
   * в щели 528..568 окно рисовалось бы серединой экрана ровно там, где до
   * нижней полосы навигации дотягивается большой палец. Заводить под эту щель
   * тринадцатый порог ширины дороже, чем сказать о размещении прямо.
   */
  sheet?: boolean;
  /**
   * Подвал: кнопки действия. Главная крайняя правая, тихая «Отмена» левее неё,
   * то есть порядок тот же, что у подвала формы на странице (`.form__foot`), и
   * правило у них буквально одно.
   *
   * ⚠️ Отдельным свойством, а не последней строкой содержимого, ровно затем,
   * чтобы подвал НЕ уезжал вместе с прокруткой тела: у окна с длинным списком
   * главная кнопка иначе прячется под нижнюю кромку.
   *
   * ⚠️ **У окна с настоящим `<form>` подвал тоже здесь**, и достигается это
   * `onSubmit` ниже, а не атрибутом `form=` у кнопки. До 14.09.2026 три таких
   * окна (смена пароля, неточность, новое занятие) держали свой ряд кнопок
   * `.form__foot` ВНУТРИ тела, то есть в прокручиваемой части: у длинной формы
   * главная кнопка пряталась под нижнюю кромку ровно тогда, когда форма
   * заполнена и её ищут. Довод «иначе теряется отправка по Enter и менеджер
   * паролей» был верен, поэтому решение не в том, чтобы вынести кнопку из формы,
   * а в том, чтобы форма ОБЪЯЛА и тело, и подвал.
   */
  foot?: React.ReactNode;
  /**
   * Отправка формы. Задан значит тело и подвал завёрнуты в настоящий `<form>`:
   * Enter отправляет, менеджер паролей видит поля, кнопка подвала остаётся
   * своей кнопкой типа `submit`.
   *
   * ⚠️ Прокрутка при этом остаётся у тела, а не уезжает к форме: строки сетки
   * коробки перекладываются на `.dlg__box--form` (разбор в `styles.css`).
   */
  onSubmit?: ((e: React.FormEvent<HTMLFormElement>) => void) | undefined;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  /** Начался ли последний `pointerdown` на самой подложке. Разбор у `onClick`. */
  const downOnBackdrop = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (el === null || el.open) return;
    el.showModal();
    const unlock = lockScroll();
    return () => {
      unlock();
      // Закрываем только то, что ещё открыто: React мог снять узел раньше нас
      if (el.open) el.close();
    };
  }, []);

  return (
    <dialog
      ref={ref}
      className={dialogClass(props)}
      aria-labelledby={titleId}
      /*
        `cancel` это Esc. Отменяем его и закрываем сами: закрытие здесь означает
        переход по адресу назад, и если дать браузеру закрыть окно самому, узел
        исчезнет, а адрес останется прежним, то есть окно вернётся при первой же
        перерисовке.

        ⚠️ `stopPropagation` обязателен (07.10.2026): нативный `cancel` не
        всплывает, а синтетический React 19 всплывает по дереву КОМПОНЕНТОВ, и
        у окна внутри окна (картинка целиком в окне озвучки) Esc закрывал бы и
        внешнее. Щелчок мимо этим не задет: там своя проверка `e.target`.
      */
      onCancel={(e) => {
        e.preventDefault();
        e.stopPropagation();
        props.onClose();
      }}
      /*
        Щелчок мимо. Целью такого щелчка оказывается сам `<dialog>`, потому что
        подложка это его псевдоэлемент `::backdrop`, а не отдельный узел. Поэтому
        у `.dlg` нулевые отступы, а всё содержимое лежит в `.dlg__box`: иначе
        щелчок по полю окна читался бы как щелчок мимо.

        ⚠️ Закрывает только щелчок, который и НАЧАЛСЯ на подложке (первый круг
        ревью 983d0f6). `click` приходит общему предку узлов нажатия и
        отпускания, то есть выделение текста в поле, отпущенное за краем окна,
        приходило целью на `<dialog>` и закрывало окно вместе с несохранённой
        правкой. Цель `pointerdown` запоминается и проверяется вместе с целью
        щелчка. У окна внутри окна (картинка целиком) нажатие по его подложке
        приходит внешнему целью внутреннего `<dialog>`, то есть внешнее не
        закрывается, как и раньше.
      */
      onPointerDown={(e) => {
        downOnBackdrop.current = e.target === ref.current;
      }}
      onClick={(e) => {
        const fromBackdrop = downOnBackdrop.current;
        downOnBackdrop.current = false;
        if (e.target === ref.current && fromBackdrop) props.onClose();
      }}
    >
      <div className={props.onSubmit === undefined ? "dlg__box" : "dlg__box dlg__box--form"}>
        <div className="dlg__bar">
          <div>
            {/*
              `h1`, а не `strong`: окно модальное, и фон под ним для читалки
              выключен (`inert` браузер ставит сам), то есть внутри окна оно и
              есть весь документ. Заголовки панелей внутри остаются `h2` и
              честно считаются от него.
            */}
            <h1 className="dlg__h" id={titleId}>
              {props.title}
            </h1>
            {props.note !== undefined && <div className="dlg__note">{props.note}</div>}
          </div>
          {/* Закрытие ничего не делает с данными, поэтому тихий вес. Мельче
              обычной: это хром окна, а не его действие */}
          <button type="button" className="btn btn--quiet btn--sm dlg__x" onClick={props.onClose}>
            Закрыть
          </button>
        </div>
        <Guts onSubmit={props.onSubmit}>
          <div className="dlg__body">{props.children}</div>
          {props.foot !== undefined && <div className="dlg__foot">{props.foot}</div>}
        </Guts>
      </div>
    </dialog>
  );
}

/**
 * Класс коробки: размещение или одна из трёх ширин.
 *
 * Отдельной функцией, а не тернарником в разметке: четыре ветки в атрибуте
 * читаются хуже, чем четыре строки подряд, и `sheet` стоит ПЕРВЫМ намеренно.
 * Лист это место, а не размер, и ширина ему не нужна ни одна из трёх; встреться
 * они в одном вызове, выигрывает размещение.
 */
function dialogClass(props: { sheet?: boolean; media?: boolean; wide?: boolean }): string {
  if (props.sheet === true) return "dlg dlg--sheet";
  if (props.media === true) return "dlg dlg--media";
  if (props.wide === true) return "dlg dlg--wide";
  return "dlg";
}

/**
 * Тело и подвал окна: либо просто два узла, либо они же внутри настоящей
 * `<form>`.
 *
 * Отдельной функцией, а не тернарником в разметке, ровно затем, чтобы узлы тела
 * и подвала были написаны ОДИН раз. Две копии одной разметки в двух ветках
 * расходятся на первой же правке, и расходятся молча: тестов на разметку в
 * проекте нет (раздел 9 CLAUDE.md).
 */
function Guts(props: {
  onSubmit?: ((e: React.FormEvent<HTMLFormElement>) => void) | undefined;
  children: React.ReactNode;
}) {
  if (props.onSubmit === undefined) return <>{props.children}</>;
  return (
    <form className="dlg__form" onSubmit={props.onSubmit}>
      {props.children}
    </form>
  );
}
